import { cn } from '../../utils/className';

export interface ChartTooltipRow {
  key: string;
  /** Machine name; secondary, the value leads */
  label: string;
  /** Reading at the hovered time, or a note such as "no data" */
  value: string;
  /** Series colour for the short line key; grey when missing */
  color?: string;
  /** Render the value in the secondary tone (off, no data) */
  muted?: boolean;
}

interface ChartTooltipProps {
  /** Pointer position inside the plot, in pixels */
  x: number;
  y: number;
  /** Plot width, so the box flips to the left near the right edge */
  containerWidth: number;
  title: string;
  rows: ChartTooltipRow[];
}

const KEY_FALLBACK = 'rgb(172, 177, 181)';

/**
 * ChartTooltip - one readout for every machine at the hovered time.
 *
 * Positioned by the caller inside a `relative` plot container. Everything shown
 * here is also visible without hovering: lanes, lines and the legend.
 */
export function ChartTooltip({
  x,
  y,
  containerWidth,
  title,
  rows,
}: ChartTooltipProps) {
  const flip = x > containerWidth - 200;

  return (
    <div
      role='status'
      className='border-border-primary bg-surface pointer-events-none absolute
        z-10 max-w-64 min-w-40 rounded-md border px-3 py-2 shadow-md'
      style={{
        top: Math.max(0, y - 8),
        left: flip ? undefined : x + 12,
        right: flip ? containerWidth - x + 12 : undefined,
      }}
    >
      <div className='size-caption text-secondary mb-1'>{title}</div>
      <ul className='flex flex-col gap-0.5'>
        {rows.map((row) => (
          <li key={row.key} className='size-caption flex items-center gap-2'>
            <span
              aria-hidden
              className='inline-block h-0.5 w-3 shrink-0 rounded-full'
              style={{ backgroundColor: row.color ?? KEY_FALLBACK }}
            />
            <span
              className={cn(
                'shrink-0 font-bold',
                row.muted ? 'text-secondary' : 'text-primary'
              )}
            >
              {row.value}
            </span>
            <span className='text-secondary truncate' title={row.label}>
              {row.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
